import { useEffect, useState } from 'react';
import { apiClient } from '../../auth-lib';
import { Button, Card, Input, Skeleton, useToast } from '../../design-system';
import WorkCalendar from './attendance/WorkCalendar';
import LeaveSettings from './attendance/LeaveSettings';
import '../styles/Attendance.css';
import { useRefreshNow } from '../../lib/useLiveRefresh';

// The Attendance card on /org-admin/settings. Four things, and no more: how
// long a standard day is, which days are working days, the year's holidays, and
// whether staff may correct their own records.

/**
 * The moment the morning nudge actually fires: the day's start minus the lead.
 *
 * ⚠️ MIRRORS jobs/attendanceDay.js#remindCheckIns, which is the authority. This
 * exists so the screen can PRINT the answer — a firm reading "10:00" and "15
 * minutes" in two separate boxes has to work out 09:45 for itself, and the one
 * that does not is the one that reports the reminder time as unchangeable.
 *
 * Wraps backwards over midnight rather than clamping at 00:00, because that is
 * what the job does: a 00:10 shift with a 15-minute lead is nudged the
 * previous evening, and printing "00:00" here would state a time nothing uses.
 */
const reminderTimeFor = (shiftStartTime, leadMinutes) => {
  const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(shiftStartTime || '');
  if (!m) return '—';
  const lead = Number.isFinite(Number(leadMinutes)) ? Math.round(Number(leadMinutes)) : 15;
  const mins = ((Number(m[1]) * 60 + Number(m[2]) - lead) % 1440 + 1440) % 1440;
  return `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;
};

const DOW = [
  { value: 1, label: 'Mon' }, { value: 2, label: 'Tue' }, { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' }, { value: 5, label: 'Fri' }, { value: 6, label: 'Sat' },
  { value: 0, label: 'Sun' },
];

function AttendanceSettingsManager() {
  const toast = useToast();
  const bump = useRefreshNow();
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newHoliday, setNewHoliday] = useState({ date: '', name: '' });

  const load = () => {
    setLoading(true);
    apiClient
      .get('/api/attendance/settings')
      .then((r) => { setData(r.data); setSettings(r.data.settings); })
      .catch((e) => toast.danger(e.response?.data?.error || 'Failed to load attendance settings'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  const canWrite = !!data?.canWrite;
  // Recomputed as they type, so changing either field shows the new moment
  // before the save rather than after it.
  const reminderAt = reminderTimeFor(
    settings?.shiftStartTime || '10:00',
    settings?.checkInReminderMinutes ?? 15
  );
  // Run state, read-only. Compared against the FIRM's today, which the server
  // sends — the browser's own date is the wrong one for a firm in another zone.
  const alreadySentToday = !!settings?.lastCheckInReminderOn
    && settings.lastCheckInReminderOn === settings.todayKey;

  const save = async () => {
    setSaving(true);
    try {
      await apiClient.put('/api/attendance/settings', settings);
      toast.success('Attendance settings saved');
      load();
      // ⚠️ The day-start dialog lives in the layout and caches these for its
      // ten-minute poll. Without this, moving the day start and then watching
      // for the prompt shows the OLD schedule for up to ten minutes — which
      // reads as the setting not having saved.
      bump();
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  // ── Office locations ──
  // Coordinates are kept as typed strings while editing so a half-entered
  // "19." doesn't become NaN and wipe the field; the server coerces on save.
  const updateOffice = (index, patch) => setSettings((s) => ({
    ...s,
    offices: (s.offices || []).map((o, i) => (i === index ? { ...o, ...patch } : o)),
  }));

  const addOffice = () => setSettings((s) => ({
    ...s,
    offices: [...(s.offices || []), { name: 'Office', lat: '', lng: '', radiusMetres: 150 }],
  }));

  const removeOffice = (index) => setSettings((s) => {
    const offices = (s.offices || []).filter((_, i) => i !== index);
    // Leaving the fence on with no office would prompt everyone for a location
    // and then mark them all remote — the server refuses it, so mirror that.
    return { ...s, offices, geofenceEnabled: offices.length ? s.geofenceEnabled : false };
  });

  // Finding a latitude on a map is the fiddly part of this whole screen, so
  // offer to read it off the device the admin is standing in the office with.
  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.danger('This browser cannot report a location');
    return navigator.geolocation.getCurrentPosition(
      (pos) => {
        setSettings((s) => ({
          ...s,
          offices: [...(s.offices || []), {
            name: 'Office',
            lat: Number(pos.coords.latitude.toFixed(6)),
            lng: Number(pos.coords.longitude.toFixed(6)),
            radiusMetres: 150,
          }],
        }));
        toast.success('Added your current location — name it and save');
      },
      () => toast.danger('Could not read your location'),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const toggleDay = (d) => setSettings((s) => ({
    ...s,
    workWeek: s.workWeek.includes(d) ? s.workWeek.filter((x) => x !== d) : [...s.workWeek, d].sort(),
  }));

  const addHoliday = () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(newHoliday.date)) { toast.danger('Pick a date'); return; }
    setSettings((s) => ({
      ...s,
      holidays: [...s.holidays.filter((h) => h.date !== newHoliday.date),
        { date: newHoliday.date, name: newHoliday.name.trim() || 'Holiday' }]
        .sort((a, b) => a.date.localeCompare(b.date)),
    }));
    setNewHoliday({ date: '', name: '' });
  };

  if (loading) return <Card><Card.Header title="🕘 Attendance" /><Skeleton.Text lines={5} /></Card>;
  if (!settings) return null;

  const hours = (settings.standardMinutesPerDay / 60).toFixed(2).replace(/\.00$/, '');

  return (
    <>
    <Card>
      <Card.Header
        title="🕘 Attendance"
        subtitle="The standard working day, the work week, and this year's holidays"
      />

      {!canWrite && (
        <p className="att-readonly">🔒 You can view these; changing them needs a manager or above.</p>
      )}

      <div className="att-set-grid">
        <Input
          label="Standard day (hours)"
          type="number" step="0.25" min="1" max="24"
          value={hours}
          disabled={!canWrite}
          onChange={(e) => setSettings((s) => ({
            ...s, standardMinutesPerDay: Math.round(Number(e.target.value || 0) * 60),
          }))}
        />
        <Input
          label="Half day below (hours)"
          type="number" step="0.25" min="0"
          value={(settings.halfDayMinutes / 60).toFixed(2).replace(/\.00$/, '')}
          disabled={!canWrite}
          onChange={(e) => setSettings((s) => ({
            ...s, halfDayMinutes: Math.round(Number(e.target.value || 0) * 60),
          }))}
        />
        <Input
          label="Timezone"
          value={settings.timezone}
          disabled={!canWrite}
          onChange={(e) => setSettings((s) => ({ ...s, timezone: e.target.value }))}
        />

        <div className="ds-field att-set-wide">
          <span className="ds-field-label">Working days</span>
          <div className="att-dow">
            {DOW.map((d) => (
              <button
                key={d.value}
                type="button"
                disabled={!canWrite}
                className={`att-dow-btn${settings.workWeek.includes(d.value) ? ' is-on' : ''}`}
                onClick={() => toggleDay(d.value)}
                aria-pressed={settings.workWeek.includes(d.value)}
              >
                {d.label}
              </button>
            ))}
          </div>
          <small className="att-hint att-hint-inline">
            A day outside the work week is a weekly off, not an absence — and work done on it is
            all overtime.
          </small>
        </div>

        {/* ── The two ends of the day ──
            Both times are read in the Timezone above, never on the server —
            the job that acts on them asks each firm what its own clock says.

            ⚠️ NOTHING HERE IS FIXED IN CODE. The reminder's moment is these
            two fields subtracted, and the helper text prints the result so
            nobody has to do the arithmetic to find out when their staff will
            actually be pinged — the commonest way a configurable setting gets
            mistaken for a hard-coded one. */}
        <Input
          label="Day starts at"
          type="time"
          value={settings.shiftStartTime || '10:00'}
          disabled={!canWrite}
          onChange={(e) => setSettings((s) => ({ ...s, shiftStartTime: e.target.value }))}
          helperText={`Staff are asked to mark the day from ${reminderAt}`}
        />
        <Input
          label="Remind this many minutes before"
          type="number" min="0" max="240" step="5"
          value={settings.checkInReminderMinutes ?? 15}
          disabled={!canWrite || settings.checkInReminderEnabled === false}
          onChange={(e) => setSettings((s) => ({
            ...s, checkInReminderMinutes: Number(e.target.value || 0),
          }))}
          helperText={`The notification goes out at ${reminderAt}`}
        />
        <Input
          label="Clock everyone out at"
          type="time"
          value={settings.autoClockOutTime || '19:00'}
          disabled={!canWrite || settings.autoClockOutEnabled === false}
          onChange={(e) => setSettings((s) => ({ ...s, autoClockOutTime: e.target.value }))}
          helperText="Anyone still clocked in is asked, then closed at this time"
        />
        <Input
          label="“Ask me later” lasts (minutes)"
          type="number" min="5" max="720" step="5"
          value={settings.dayPromptSnoozeMinutes ?? 60}
          disabled={!canWrite || settings.dayPromptEnabled === false}
          onChange={(e) => setSettings((s) => ({
            ...s, dayPromptSnoozeMinutes: Number(e.target.value || 0),
          }))}
          helperText="How long the dialog stays away when somebody skips it"
        />

        <label className="att-set-toggle att-set-wide">
          <input
            type="checkbox"
            checked={settings.checkInReminderEnabled !== false}
            disabled={!canWrite}
            onChange={(e) => setSettings((s) => ({ ...s, checkInReminderEnabled: e.target.checked }))}
          />
          <span>
            <b>Remind staff to clock in each morning</b>
            <small>
              A notification at {reminderAt} ({settings.checkInReminderMinutes ?? 15} minutes
              before {settings.shiftStartTime || '10:00'}), to everyone who has not clocked in
              yet. Nobody on approved leave is nudged, and nothing is sent on a holiday or a
              weekly off.
            </small>
            {/* ⚠️ THE ANSWER TO "WHY DID NOTHING ARRIVE?". The reminder is
                claimed once per day, so once it has gone out, moving the time
                cannot fire it again on the same day — and every field on this
                screen goes on describing a notification that will not come.
                Saving a new time clears the claim, which is what the second
                sentence is telling them to do. */}
            {alreadySentToday && (
              <small className="att-hint-warn">
                Today’s reminder has already gone out. Changing the time above and saving will
                let it fire again today; otherwise the next one is tomorrow.
              </small>
            )}
          </span>
        </label>

        <label className="att-set-toggle att-set-wide">
          <input
            type="checkbox"
            checked={settings.autoClockOutEnabled !== false}
            disabled={!canWrite}
            onChange={(e) => setSettings((s) => ({ ...s, autoClockOutEnabled: e.target.checked }))}
          />
          <span>
            <b>Close anyone still clocked in at {settings.autoClockOutTime || '19:00'}</b>
            <small>
              A day nobody closed reads as an endless shift. Hours worked after that are
              <b> not lost and not guessed</b> — the session is marked as auto-closed on the
              register and the person is told to add the extra time by hand.
            </small>
          </span>
        </label>

        <label className="att-set-toggle att-set-wide">
          <input
            type="checkbox"
            checked={settings.dayPromptEnabled !== false}
            disabled={!canWrite}
            onChange={(e) => setSettings((s) => ({ ...s, dayPromptEnabled: e.target.checked }))}
          />
          <span>
            <b>Ask staff to mark the day, in the app</b>
            <small>
              A dialog on whatever page they have open: <b>at the office, working from home, or
              on leave</b> from {' '}
              {settings.checkInReminderMinutes ?? 15} minutes before {settings.shiftStartTime || '10:00'},
              and <b>&ldquo;still in the office?&rdquo;</b> at {settings.autoClockOutTime || '19:00'} for
              anyone whose clock was closed for them. It never blocks the page — skipping it asks
              again in {settings.dayPromptSnoozeMinutes ?? 60} minutes — and it is silent on
              holidays, weekly offs and approved leave. The evening half needs the auto-close above.
            </small>
          </span>
        </label>

        <label className="att-set-toggle att-set-wide">
          <input
            type="checkbox"
            checked={!!settings.allowSelfEdit}
            disabled={!canWrite}
            onChange={(e) => setSettings((s) => ({ ...s, allowSelfEdit: e.target.checked }))}
          />
          <span>
            <b>Staff may correct their own attendance</b>
            <small>Off by default. A manager can always correct anyone's, and every correction is stamped with who and why.</small>
          </span>
        </label>

        <label className="att-set-toggle att-set-wide">
          <input
            type="checkbox"
            checked={settings.allowSelfManualEntry !== false}
            disabled={!canWrite}
            onChange={(e) => setSettings((s) => ({ ...s, allowSelfManualEntry: e.target.checked }))}
          />
          <span>
            <b>Staff may add hours they forgot to clock</b>
            <small>
              On by default — people are trusted to record their own day. Hand-entered hours are
              marked as such on the register and on your team board, so you can still see them.
            </small>
          </span>
        </label>
      </div>

      {/* ── Office geofence ── */}
      <h4 className="att-set-h">Office locations</h4>
      <p className="att-hint">
        A clock-in inside one of these circles is recorded as <b>office</b> attendance; anywhere
        else is recorded as <b>remote</b>. It never blocks anyone — GPS is unreliable indoors, so
        someone whose phone can't get a fix is simply marked remote rather than being unable to
        record their day.
      </p>

      <label className="att-set-toggle att-set-wide">
        <input
          type="checkbox"
          checked={!!settings.geofenceEnabled}
          disabled={!canWrite || (settings.offices || []).length === 0}
          onChange={(e) => setSettings((s) => ({ ...s, geofenceEnabled: e.target.checked }))}
        />
        <span>
          <b>Ask for location when clocking in</b>
          <small>
            {(settings.offices || []).length === 0
              ? 'Add an office below first — with none set, everyone would be marked remote anyway.'
              : 'Off means no location prompt at all, and every day is recorded as remote.'}
          </small>
        </span>
      </label>

      {(settings.offices || []).length === 0 ? (
        <p className="att-office-empty">No office locations yet.</p>
      ) : (
        (settings.offices || []).map((o, i) => (
          <div className="att-office-row" key={i}>
            <Input
              label="Name" id={`office-name-${i}`} value={o.name || ''} disabled={!canWrite}
              onChange={(e) => updateOffice(i, { name: e.target.value })}
            />
            <Input
              label="Latitude" id={`office-lat-${i}`} type="number" step="any" value={o.lat ?? ''} disabled={!canWrite}
              onChange={(e) => updateOffice(i, { lat: e.target.value })}
            />
            <Input
              label="Longitude" id={`office-lng-${i}`} type="number" step="any" value={o.lng ?? ''} disabled={!canWrite}
              onChange={(e) => updateOffice(i, { lng: e.target.value })}
            />
            <Input
              label="Radius (m)" id={`office-rad-${i}`} type="number" value={o.radiusMetres ?? 150} disabled={!canWrite}
              onChange={(e) => updateOffice(i, { radiusMetres: e.target.value })}
            />
            {canWrite && (
              <Button variant="secondary" size="sm" onClick={() => removeOffice(i)}>Remove</Button>
            )}
          </div>
        ))
      )}

      {canWrite && (
        <div className="att-manual-actions">
          <Button variant="secondary" size="sm" onClick={addOffice}>Add office</Button>
          {/* The coordinates are the fiddly part, so offer to read them from the
              device rather than making someone find them on a map. */}
          <Button variant="secondary" size="sm" onClick={useMyLocation}>Use my current location</Button>
        </div>
      )}

      <h4 className="att-set-h">Working year</h4>
      <p className="att-hint">
        A listed day is a holiday for everyone — it owes no hours, so it never shows as an absence.
        The calendar reflects the settings above <b>as you edit them</b>, before you save.
      </p>

      {canWrite && (
        <div className="att-hol-add">
          <input
            type="date" className="ds-input"
            value={newHoliday.date}
            onChange={(e) => setNewHoliday((h) => ({ ...h, date: e.target.value }))}
          />
          <input
            className="ds-input" placeholder="Name (e.g. Diwali)"
            value={newHoliday.name}
            onChange={(e) => setNewHoliday((h) => ({ ...h, name: e.target.value }))}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addHoliday(); } }}
          />
          <Button variant="secondary" size="sm" onClick={addHoliday}>
            {settings.holidays.some((h) => h.date === newHoliday.date) ? 'Update' : 'Add'}
          </Button>
        </div>
      )}

      <WorkCalendar
        settings={settings}
        canWrite={canWrite}
        // Tapping a day loads it into the row above — including its existing
        // name, so clicking a holiday edits it rather than silently replacing
        // the reason with "Holiday".
        onPickDate={(date, name) => setNewHoliday({ date, name })}
        onRemoveHoliday={(date) => setSettings((s) => ({
          ...s, holidays: s.holidays.filter((x) => x.date !== date),
        }))}
      />

      {settings.holidays.length > 0 && (
        <p className="att-hint att-hint-inline">
          {settings.holidays.length} holiday{settings.holidays.length === 1 ? '' : 's'} set in total.
        </p>
      )}

      {canWrite && (
        <div className="att-set-actions">
          <Button variant="primary" loading={saving} onClick={save}>Save attendance settings</Button>
        </div>
      )}
    </Card>

    {/* Leave routing and the type catalog. Its own card, and its own save —
        the two answer different questions and a single Save spanning both
        would make an unrelated edit ride along with the one you meant. */}
    <LeaveSettings />
    </>
  );
}

export default AttendanceSettingsManager;
